import React, { useEffect, useState } from 'react';
import './Alert.css'; 
import Navbar from '../Navbar/Navbar';
import RequestDeleteModal from '../Modal/DeleteRequest/DeleteRequest';
import { useTheme } from '../ThemeContext';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/apiClient';
import { useData } from '../../context/DataContext';
import Loader from '../Loader/Loader';

const ITEMS_PER_PAGE = 9;

const Alert = () => {
  const { requests: cachedRequests, refreshRequests } = useData();
  const [requests, setRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletionResult, setDeletionResult] = useState('');
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();
  const { user } = useAuth();


  useEffect(() => {
    const data = cachedRequests || [];
    setRequests(data);
    setTotalPages(Math.ceil(data.length / ITEMS_PER_PAGE) || 1);
    setLoading(false);
  }, [cachedRequests]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await refreshRequests();
      } finally {
        setLoading(false);
      }
    };
    load();
    const interval = setInterval(() => {
      refreshRequests();
    }, 15000);
    return () => clearInterval(interval);
  }, [refreshRequests]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDelete = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

  const handleViewAttachment = async (request) => {
    try {
      const res = await apiClient.get(`/api/requests/${request._id}/attachment`);
      if (res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not open attachment.';
      window.alert(msg);
    }
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await apiClient.delete(`/api/requests/${selectedRequest._id}`, {
        params: { organization: user?.organizationId || user?.organization || 'default' }
      });
      setDeletionResult('Request deleted successfully');
      const updated = requests.filter(request => request._id !== selectedRequest._id);
      setRequests(updated);
      setTotalPages(Math.ceil((updated.length) / ITEMS_PER_PAGE) || 1);
      await refreshRequests();
    } catch (error) {
      setDeletionResult('Error deleting request');
    } finally {
      setIsDeleting(false);
      setShowModal(false);
    }
  };

  const paginatedRequests = requests.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className={`alert-container ${theme}`}>
      <Navbar /> 
      <div className="alert-content page-transition">
        <div className="page-shell alert-page">
          <h1 className="page-title">Alert</h1>
          {loading ? (
            <Loader label="Loading requests..." />
          ) : paginatedRequests.length > 0 ? (
            <>
              <div className="alert-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>URL</th>
                      <th>Message</th>
                      <th>Details</th>
                      <th>Email</th>
                      <th>Attachment</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRequests.map(request => (
                      <tr key={request._id}>
                        <td>{request.url}</td>
                        <td>{request.message}</td>
                        <td>{request.details}</td>
                        <td>{request.email}</td>
                        <td>
                          {request.attachment ? (
                            <button
                              className='view-button'
                              onClick={() => handleViewAttachment(request)}
                            >
                              View
                            </button>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td>
                          <button className='delete-button' onClick={() => handleDelete(request)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="pagination">
                {Array.from({ length: totalPages }, (_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => handlePageChange(index + 1)}
                    className={`page-button ${currentPage === index + 1 ? 'active' : ''}`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p>{loading ? 'Loading requests...' : 'No requests found.'}</p>
          )}
        </div>
      </div>

      <RequestDeleteModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={confirmDelete}
        request={selectedRequest}
        isDeleting={isDeleting}
        deletionResult={deletionResult}
      />
    </div>
  );
};

export default Alert;
